/**
 * Section A5 Page
 * A.5.技術及措施
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionA5Service, { SectionA5Detail } from '../services/sectionA5Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionA5Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [sectionA5Data, setSectionA5Data] = useState<SectionA5Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const emptyForm = { project_existed_tech: '', project_deployed_tech: '', project_former: '' };

  // ========== 表單狀態 ==========
  const [formData, setFormData] = useState(emptyForm);
  const [originalData, setOriginalData] = useState(emptyForm);

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionA5Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setSectionA5Data(null);
      setFormData(emptyForm);
      setOriginalData(emptyForm);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const data = await sectionA5Service.getSectionA5(selectedProjectId);

        if (data) {
          setSectionA5Data(data);
          const loadedData = {
            project_existed_tech: data.project_existed_tech || '',
            project_deployed_tech: data.project_deployed_tech || '',
            project_former: data.project_former || ''
          };
          setFormData(loadedData);
          setOriginalData(loadedData);
        } else {
          setSectionA5Data(null);
          setFormData(emptyForm);
          setOriginalData(emptyForm);
        }
      } catch (err: any) {
        console.error('載入 A.5 資料失敗:', err);
        setError(err.response?.data?.detail || '載入資料失敗');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId]);

  // ========== 重置 ==========
  const handleReset = async () => {
    const ok = await toast.confirm({
      title: i18n.language === 'zh-TW' ? '操作確認' : 'Confirmation',
      message: i18n.language === 'zh-TW' ? '確定要重置為原始資料嗎？' : 'Are you sure you want to reset to original data?',
      confirmText: i18n.language === 'zh-TW' ? '重設' : 'Reset',
    });
    if (!ok) return;
    setFormData(originalData);
    setError(null);
    setSuccess(null);
  };

  // ========== 儲存 ==========
  const handleSave = async () => {
    if (!selectedProjectId) {
      setError(i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const result = await sectionA5Service.saveSectionA5(selectedProjectId, {
        project_existed_tech: formData.project_existed_tech || undefined,
        project_deployed_tech: formData.project_deployed_tech || undefined,
        project_former: formData.project_former || undefined
      });

      const updatedData = await sectionA5Service.getSectionA5(selectedProjectId);
      setSectionA5Data(updatedData);

      const savedData = {
        project_existed_tech: formData.project_existed_tech || '',
        project_deployed_tech: formData.project_deployed_tech || '',
        project_former: formData.project_former || ''
      };
      setOriginalData(savedData);
      setSuccess(result.message || (i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully'));
      toast.success(result.message || (i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully'));

    } catch (err: any) {
      console.error('儲存失敗:', err);
      const errorMsg = err.response?.data?.detail || '儲存失敗';
      setError(errorMsg);
      toast.error(i18n.language === 'zh-TW' ? `儲存失敗: ${errorMsg}` : `Save failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('section_a5', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  const canEdit = hasPermission('section_a5', 'update');

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_a5" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '16px' }}>{success}</div>}

      {selectedProjectId && (
        <div style={{ marginBottom: '24px' }}>
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  {i18n.language === 'zh-TW' ? 'A.5.1.既有技術' : 'A.5.1.Existing Technology'}
                </label>
                <RichTextEditor
                  value={formData.project_existed_tech}
                  onChange={(html) => setFormData({ ...formData, project_existed_tech: html })}
                  placeholder={i18n.language === 'zh-TW' ? '請輸入既有技術...' : 'Please enter existing technology...'}
                  disabled={!canEdit}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  {i18n.language === 'zh-TW' ? 'A.5.2.專案技術' : 'A.5.2.Deployed Technology'}
                </label>
                <RichTextEditor
                  value={formData.project_deployed_tech}
                  onChange={(html) => setFormData({ ...formData, project_deployed_tech: html })}
                  placeholder={i18n.language === 'zh-TW' ? '請輸入專案技術...' : 'Please enter deployed technology...'}
                  disabled={!canEdit}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  {i18n.language === 'zh-TW' ? 'A.5.3.專案聲明' : 'A.5.3.Project Statement'}
                </label>
                <RichTextEditor
                  value={formData.project_former}
                  onChange={(html) => setFormData({ ...formData, project_former: html })}
                  placeholder={i18n.language === 'zh-TW' ? '請輸入專案聲明...' : 'Please enter project statement...'}
                  disabled={!canEdit}
                />
              </div>

              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? (i18n.language === 'zh-TW' ? '儲存中...' : 'Saving...') : (i18n.language === 'zh-TW' ? '儲存' : 'Save')}
                  </button>
                  <button className="btn-secondary" onClick={handleReset} disabled={loading}>
                    {i18n.language === 'zh-TW' ? '重置' : 'Reset'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionA5Page;
